import { useState, useEffect } from 'react';
import { Conference } from "../../types/conference";
import { ConferenceGroup, ConferenceGroupResponse } from "../../types/conferenceGroup";
import { isPasscode } from "../../../lib/validate";
import { filterPasscode } from "../../../lib/filter";
import Button from "@mui/material/Button";
import { APIHOST } from "../../common";
import { useAuth, getToken } from '../../components/auth/AuthContext';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import { set } from 'date-fns';
import { useApp } from '../../components/AppContext';



const ConferenceCard = ({ conference, conferenceGroup }: { conference: Conference, conferenceGroup: ConferenceGroup }) => {
  const { isLoggedIn, showAuthDialog } = useAuth();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const { setSnackbarInput } = useApp();

  const createOrderAndRedirect = async () => {
    try {
      setCreating(true);
      setError('');
      
      const authToken = getToken();
      if (!authToken) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/orders/new`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({
          accessCode: conferenceGroup.accessCode
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create order');
      }

      const orderData = await response.json();
      setSnackbarInput({ message: 'New order started!', severity: 'success' });
      // Redirect to registration with the order ID
      window.location.href = `/registration?orderId=${orderData.data.documentId}`;
      
    } catch (err) {
      console.error('Error creating order:', err);
      setError(err instanceof Error ? err.message : 'Failed to create order');
      setSnackbarInput({ message: err instanceof Error ? err.message : 'Failed to create order', severity: 'error' });
    } finally {
      setCreating(false);
    }
  };
  

    if (conferenceGroup.allowLoginsAfterDateTime) {
      const now = new Date();
      const allowLoginsAfter = new Date(conferenceGroup.allowLoginsAfterDateTime);
      if (now < allowLoginsAfter) {
        return (
          <Alert severity="info">
            Registration will open on {allowLoginsAfter.toLocaleString()}
          </Alert>
        );
      }
    } 

    return (
      <Card>
        {/* <CardMedia component="img" image={`${APIHOST}${conference.logo?.formats.small?.url}`} alt={conference.longName} /> */}
        <CardHeader title={conference.longName} subheader={conferenceGroup.longName}/> 
        <CardContent>
          {isLoggedIn ? (
            <>Conference found! Click below to start your registration.</>
          ) : (
            <>Conference found! Create an account/sign in to continue with your hotel registration.</>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          {isLoggedIn ? (
            <Button 
              size="small" 
              onClick={createOrderAndRedirect}
              variant="contained"
              disabled={creating}
            >
              {creating ? <CircularProgress size={20} sx={{ ml: 1 }} /> : null}
              Get Started
            </Button>
          ) : (
            <Button size="small" onClick={() => showAuthDialog()} variant="contained">
              Create Account / Login
            </Button>
          )}
        </CardActions>
      </Card>
    );
}



const RegistrationStart = () => {
  
  const [passcode, setPasscode] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [conference, setConference] = useState<Conference | null>(null);
  const [conferenceGroup, setConferenceGroup] = useState<ConferenceGroup>({} as ConferenceGroup);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { setSnackbarInput } = useApp();

  useEffect(() => {
    // Check for passcode in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const urlPasscode = urlParams.get('passcode');
    
    if (urlPasscode) {
      // Filter and validate the URL passcode
      const filteredValue = filterPasscode(urlPasscode);
      setPasscode(filteredValue);
      // Validate the passcode
      const valid = isPasscode(filteredValue);
      setIsValid(valid);
      
      // If passcode is valid, fetch conference group data
      if (valid) {
        fetchConferenceGroup(filteredValue);
      }
    }
  }, []);

  const fetchConferenceGroup = async (accessCode: string) => {
   
    try {
      setLoading(true);
      setError("");
      setSnackbarInput({ message: 'Looking up conference...', severity: 'info' });
      const response = await fetch(`${APIHOST}/api/conference-groups?filters[accessCode][$eq]=${accessCode}&populate[]=conference&populate[]=conference.logo`);
      
      if (!response.ok) {
        setSnackbarInput({ message: 'Failed to fetch conference data', severity: 'error' });
        throw new Error('Failed to fetch conference group data');
      }
      
      const data: ConferenceGroupResponse = await response.json();
      const group = data.data[0] || null;
      setConferenceGroup(group);
      
      // Extract conference from the group
      if (group && (group as any).conference) {
        setConference((group as any).conference);
        setSnackbarInput({ message: 'Conference found!', severity: 'success' });
      } else {
        setConference(null);
        setSnackbarInput({ message: 'No conference found for this passcode', severity: 'error' });
      }
      
      console.debug('Conference Group data:', group);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setSnackbarInput({ message: err instanceof Error ? err.message : 'An error occurred', severity: 'error' });
      console.error('Error fetching conference group:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePasscodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {

    const filteredValue = filterPasscode(e.target.value);
    setPasscode(filteredValue);
    const valid = isPasscode(filteredValue);
    setIsValid(valid);
    
    // Clear previous conference data when passcode changes
    setConference(null);
    setConferenceGroup({} as ConferenceGroup);
    setError("");
    
    // If passcode is valid, fetch conference group data
    if (valid) {
      fetchConferenceGroup(filteredValue);
    }
  };

  console.debug('Rendering RegistrationStart with state:', { passcode, isValid, conference, conferenceGroup, loading, error });
  return (
    <Box sx={{ width: '100%', maxWidth: '400px' }}>
      <Typography 
        variant="h6" 
        component="h6" 
        fontWeight="600"
        sx={{ 
          maxWidth: '400px',
          lineHeight: 1.3,
          mb: 2
        }}
      >
        Enter Passcode to get started.
      </Typography>
      
      <TextField
        fullWidth
        value={passcode}
        onChange={handlePasscodeChange}
        color={
          passcode.length === 0 
            ? 'primary'
            : isValid 
              ? 'success' 
              : 'error'
        }
        error={passcode.length > 0 && !isValid}
        sx={{
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
              borderColor: passcode.length === 0 
                ? 'primary.main'
                : isValid 
                  ? 'success.main' 
                  : 'error.main'
            }
          }
        }}
      />
      
      {passcode.length > 0 && !isValid && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Passcode should be letters and numbers only, no spaces or special characters.
        </Alert>
      )}


    {loading && <Box>
        <CircularProgress size={16} />
        <Typography variant="body2" color="primary">
          Loading conference data...
        </Typography>
    </Box>}
  
    {conference && <ConferenceCard conference={conference} conferenceGroup={conferenceGroup} />}  

    {error && (
      <Alert severity="error" sx={{ mt: 2 }}>
        Conference not found, double check access code
      </Alert>
    )}

  </Box>
  );
  
};
   
export default RegistrationStart;